import { motion } from 'framer-motion';

export type FaqItem = {
  question: string;
  answer: string;
};

type Props = {
  faqs?: FaqItem[];
  ctaHref?: string;
};

const fallbackFaqs: FaqItem[] = [
  {
    question: 'How are LINDY kittens socialized before adoption?',
    answer:
      'From day 5 we begin gentle handling, purring meditations, and sound desensitization. Weekly enrichment salons ensure every kitten meets new textures, toys, and friendly visitors.',
  },
  {
    question: 'What does the lifetime concierge support include?',
    answer:
      'You receive a direct line to our caretaking team for nutrition adjustments, grooming guidance, enrichment calendars, and travel planning—whenever your companion needs a little extra care.',
  },
  {
    question: 'When can families meet their matched kitten?',
    answer:
      'Virtual introductions begin around week 6 with curated photo essays and live check-ins. In-person meetups are coordinated between weeks 10 and 12 depending on the kitten’s temperament readiness.',
  },
];

export default function FaqPreview({ faqs = [], ctaHref = '/faq' }: Props) {
  const data = faqs.length ? faqs.slice(0, 3) : fallbackFaqs;

  return (
    <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white via-soft-gold/10 to-cream p-10 shadow-[0_25px_60px_rgba(221,203,168,0.35)]">
      <motion.div
        className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-soft-gold/25 blur-3xl"
        animate={{ opacity: [0.2, 0.4, 0.2], scale: [0.9, 1.05, 0.9] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="pointer-events-none absolute left-1/3 bottom-[-120px] h-72 w-72 rounded-full bg-amber-200/20 blur-3xl"
        animate={{ opacity: [0.18, 0.32, 0.18], scale: [0.85, 1.08, 0.92] }}
        transition={{ duration: 13, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
      />

      <div className="relative z-10 flex flex-col gap-8">
        <div className="text-center md:text-left md:flex md:items-end md:justify-between gap-6">
          <div>
            <motion.span
              className="inline-flex items-center gap-2 rounded-full border border-soft-gold/40 bg-white/80 px-4 py-2 text-xs uppercase tracking-[0.35em] text-warm-brown"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Concierge Answers
            </motion.span>
            <motion.h2
              className="mt-4 text-3xl md:text-4xl font-heading font-semibold text-warm-brown"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Essentials our families ask before homecoming
            </motion.h2>
          </div>
          <motion.a
            href={ctaHref}
            className="inline-flex items-center gap-2 rounded-full border border-soft-gold/40 px-5 py-2 text-sm font-semibold text-warm-brown transition-all hover:border-soft-gold hover:bg-soft-gold/90 hover:text-white"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            View full FAQ
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </motion.a>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {data.map((faq, index) => (
            <motion.article
              key={faq.question}
              className="group relative overflow-hidden rounded-2xl border border-soft-gold/30 bg-white/85 p-6 backdrop-blur-xl"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.12 }}
              whileHover={{ y: -6 }}
            >
              <motion.div
                className="pointer-events-none absolute inset-0 rounded-2xl"
                style={{ background: 'linear-gradient(160deg, rgba(212,170,91,0.25), rgba(255,255,255,0))' }}
                initial={{ opacity: 0.15 }}
                animate={{ opacity: [0.15, 0.35, 0.15] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: index * 0.2 }}
              />
              <div className="relative space-y-4">
                <motion.span
                  className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-soft-gold"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.05 }}
                >
                  FAQ {index + 1 < 10 ? `0${index + 1}` : index + 1}
                  <span className="h-px w-8 bg-soft-gold/60" />
                </motion.span>
                <motion.h3
                  className="text-xl font-semibold text-warm-brown"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.1 }}
                >
                  {faq.question}
                </motion.h3>
                <motion.p
                  className="text-sm leading-relaxed text-warm-brown/70"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.18 }}
                >
                  {faq.answer}
                </motion.p>
                <motion.span
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-soft-gold"
                  animate={{ opacity: [0.4, 0.9, 0.4] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: index * 0.3 }}
                >
                  Learn more
                  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </motion.span>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
